import React, { useEffect } from 'react';
import { Box, CssBaseline, Typography, IconButton, useTheme, useMediaQuery } from '@mui/material';
import { Menu as MenuIcon } from '@mui/icons-material';
import Sidebar from './Sidebar/Sidebar';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import TopNavigationTabs from './TopNavigationTabs';
import { useTabContext } from '../contexts/TabContext';
import { useLocation } from 'react-router-dom';
import { getRouteByPath } from '../config/routes';

interface LayoutProps {
  // Children is no longer needed as we render based on tabs
  children?: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = () => {
  const [isCollapsed, setIsCollapsed] = React.useState(false);
  const { addTab, tabs, activeTabPath } = useTabContext();
  const { dir, t } = useLanguage();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const location = useLocation();

  // Initialize closed on mobile
  useEffect(() => {
    if (isMobile) {
      setIsCollapsed(true);
    }
  }, [isMobile]);

  // Register tab on mount or path change
  useEffect(() => {
    // Only register if it's a known route
    let routeConfig = getRouteByPath(location.pathname);

    // If not found, try to strip the app name (first segment)
    if (!routeConfig) {
      const parts = location.pathname.split('/');
      if (parts.length > 2) {
        // Example: /Planning/dashboard -> /dashboard
        const subPath = '/' + parts.slice(2).join('/');
        routeConfig = getRouteByPath(subPath);
      }
    }

    if (routeConfig) {
      addTab({
        id: location.pathname,
        path: location.pathname,
        title: routeConfig.title,
        translationKey: routeConfig.translationKey,
        closable: routeConfig.closable,
        component: routeConfig.component,
        icon: routeConfig.icon
      });
    }
  }, [location.pathname, addTab]);

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />

      {/* Sidebar Navigation */}
      <Sidebar isCollapsed={isCollapsed} onToggle={() => setIsCollapsed(!isCollapsed)} />

      {/* Main Content Area */}
      {/* Main Content Area */}
      <Box
        component="main"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flexGrow: 1,
          backgroundColor: (theme) => theme.palette.background.default, // Use theme background
          height: '100vh', // Fixed height to viewport
          overflow: 'hidden', // Prevent window scroll
          width: '100%', // Flexible width
          transition: (theme) => theme.transitions.create('margin', {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.leavingScreen,
          }),
        }}
      >
        <TopNavigationTabs
          onToggleSidebar={() => setIsCollapsed(!isCollapsed)}
          isMobile={isMobile}
        />

        <Box
          className="layout-tab-viewport"
          sx={{
            flexGrow: 1,
            p: 3,
            position: 'relative',
            overflow: 'hidden', // Ensure children handle their own scroll
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0
          }}
        >
          {/* Iterate over all open tabs and render them, hiding inactive ones */}
          {tabs.map(tab => {
            const isActive = tab.path === activeTabPath;
            return (
              <Box
                key={tab.path}
                className={`layout-tab-content ${isActive ? 'layout-tab-active' : 'layout-tab-inactive no-print'}`}
                sx={{
                  display: isActive ? 'flex' : 'none',
                  flexDirection: 'column',
                  height: '100%',
                  minHeight: 0,
                  flexGrow: 1,
                }}
              >
                {tab.component}
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box
          component="footer"
          className="no-print"
          sx={{
            py: 1,
            px: 2,
            mt: 'auto',
            textAlign: 'center',
            borderTop: '1px solid',
            borderColor: 'divider'
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('footer.copyright')}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;

